import { useState } from "react";
import { Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverAnchor, PopoverContent } from "@workspace/ui/components/ui/popover";
import type { AvailabilityData } from "@/lib/api";
import { quarterHourStartOptions } from "@/lib/hourly-booking";
import { SingleDateCalendar } from "./SingleDateCalendar";

interface EventTripFieldsProps {
  dateFrom: string;
  dateTo: string;
  pickupTime: string;
  dropoffTime: string;
  twelveHour: boolean;
  mobile?: boolean;
  availability?: AvailabilityData | null;
  onDateChange: (field: "start" | "end", date: string) => void;
  onTimeChange: (field: "start" | "end", time: string) => void;
}

/** Explicit event interval: two independent dates, never capped at 24 hours. */
export function EventTripFields({
  dateFrom, dateTo, pickupTime, dropoffTime, twelveHour, mobile = false,
  availability, onDateChange, onTimeChange,
}: EventTripFieldsProps) {
  const { t, i18n } = useTranslation();
  const [calendarField, setCalendarField] = useState<"start" | "end" | null>(null);
  const labelCls = "text-[10px] font-bold uppercase tracking-wider text-muted-foreground";
  const controlCls = "relative flex min-h-11 min-w-0 w-full items-center gap-2 rounded-md border border-border bg-background px-2.5 text-left text-sm hover:bg-muted/50";
  const dateLabel = (date: string) => date
    ? new Intl.DateTimeFormat(i18n?.resolvedLanguage || i18n?.language || "en", {
      weekday: "short", day: "numeric", month: "short", year: mobile ? undefined : "numeric", timeZone: "UTC",
    }).format(new Date(`${date}T12:00:00Z`))
    : t("booking.select_date", "Select date");
  const selectedDate = calendarField === "end" ? dateTo : dateFrom;
  const calendar = calendarField && (
    <SingleDateCalendar
      key={calendarField}
      value={selectedDate || dateFrom}
      availability={availability}
      rangeStart={calendarField === "end" ? dateFrom : undefined}
      onSelect={date => {
        onDateChange(calendarField, date);
        setCalendarField(null);
      }}
    />
  );

  return (
    <div className="space-y-2" data-testid="booking-event-fields">
      {(["start", "end"] as const).map(field => {
        const isStart = field === "start";
        const date = isStart ? dateFrom : dateTo;
        const time = isStart ? pickupTime : dropoffTime;
        const options = quarterHourStartOptions(twelveHour, time);
        const dateName = isStart ? t("booking.event_start_date", "Start date") : t("booking.event_end_date", "End date");
        const timeName = isStart ? t("booking.start_time", "Start time") : t("booking.event_end_time", "End time");
        const button = (
          <button type="button" className={controlCls} aria-label={dateName}
            aria-expanded={calendarField === field} data-testid={`booking-event-${field}-date`}
            onClick={() => setCalendarField(current => current === field ? null : field)}>
            <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="min-w-0 truncate">{dateLabel(date)}</span>
          </button>
        );
        return (
          <Popover key={field} open={!mobile && calendarField === field}
            onOpenChange={open => setCalendarField(open ? field : null)}>
            <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-2">
              <div className="min-w-0">
                <p className={labelCls}>{dateName}</p>
                <div className="mt-1">
                  {mobile ? button : <PopoverAnchor asChild>{button}</PopoverAnchor>}
                </div>
              </div>
              <div className="min-w-0">
                <p className={labelCls}>{timeName}</p>
                <select value={time} aria-label={timeName} data-testid={`select-event-${field}-time`}
                  onChange={e => onTimeChange(field, e.target.value)}
                  className="mt-1 block h-11 w-full min-w-0 cursor-pointer rounded-md border border-border bg-background px-2.5 text-sm text-foreground hover:bg-muted/50">
                  {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </div>
            </div>
            {!mobile && calendarField === field && (
              <PopoverContent align="start" className="w-[320px] p-3">{calendar}</PopoverContent>
            )}
          </Popover>
        );
      })}
      {mobile && calendar && <div className="rounded-lg border border-border bg-background p-2">{calendar}</div>}
    </div>
  );
}
